import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiServer from "../../constants/apiServer";
import { put } from "../../utils/api";
import format from "date-fns/format";
import AdaAlert from "../../common/AdaAlert";
import PopupLoading from "../../common/PopupLoading";
import "../../css/artist.sass";
import useFetchInfo from "../../utils/useFetchInfo";
import { img_url_api } from "../../constants/apiImg";
import ArtistForm from "./ArtistForm";
import { checkFormValidate } from "../../utils/commonFuncs";
import EmptyPage from "../../common/EmptyPage";
import SquareLoading from "../../common/SquareLoading";
import { handleApiError, handleApiSuccess } from "../../utils/errorHandler";

const UpdateArtist = () => {
    const { artist_Id } = useParams();
    const navigate = useNavigate();

    const [artistInfoValues, setArtistInfoValues] = useState({
        profile: null,
        nameENG: "",
        nameMM: "",
        type: [],
        chosenType: {},
        artist_type_id: 0,
        dob: new Date("1800-1-1"),
        gender: "male",
        bioENG: "",
        bioMM: "",
    });    

    const [errorValues, setErrorValues] = useState({});

    const [getInfoLoading, setGetInfoLoading] = useState(true);
    const [artistUpdateLoading, setArtistUpdateLoading] = useState(false);
    const [errorUpdateArtist, setErrorUpdateArist] = useState("");

    const [getInfoError, setGetInfoError] = useState("");

    const {
        data: typeList,
        isLoading: typeListLoading,
        error: typeListError,
    } = useFetchInfo(`${apiServer.artist_type.all_list}`);

    const {
        data: artistInfo,
        isLoading: artistInfoLoading,
        error: artistInfoError,
    } = useFetchInfo(`${apiServer.artists.detail}/${artist_Id}`);

    useEffect(() => {
        setGetInfoLoading(typeListLoading && artistInfoLoading);
        setGetInfoError(typeListError || artistInfoError);
        const modifyData = [];
        typeList.forEach((element) => {
            const artistTypeName = element["name"];
            const engName = artistTypeName?.eng || artistTypeName?.mm || '';
            const mmName = artistTypeName?.mm || artistTypeName?.eng || '';
            modifyData.push({
                value: element?.id,
                label: `${engName} : ${mmName}`,
            });
        });        

        setArtistInfoValues({
            profile: img_url_api + artistInfo?.image,
            nameENG: artistInfo?.name?.eng || artistInfo?.name?.mm || "",
            nameMM: artistInfo?.name?.mm || artistInfo?.name?.eng || "",
            type: modifyData,
            chosenType: {
                value: artistInfo?.artist_type_id,
                label: `${artistInfo?.artistType?.name?.eng || artistInfo?.artistType?.name?.mm || ''} : ${artistInfo?.artistType?.name?.mm || artistInfo?.artistType?.name?.eng || ''}`,
            },
            artist_type_id: artistInfo?.artist_type_id,
            dob: new Date(artistInfo?.dob),
            gender: artistInfo?.gender == "male" ? "male" : "female",
            bioENG: artistInfo?.bio?.eng || artistInfo?.bio?.mm || "",
            bioMM: artistInfo?.bio?.mm || artistInfo?.bio?.eng || "",
        });
    }, [
        typeListLoading,
        artistInfoLoading,
        typeListError,
        artistInfoError,
        artistInfo,
        typeList,
    ]);

    const handleCancelUpdate = () => {
        navigate("/artists", { replace: true });
    };

    const handleUploadImage = (imageInfo) => {
        setArtistInfoValues((prevInfo) => {
            return {
                ...prevInfo,
                profile: imageInfo,
            };
        });
        delete errorValues["profile"];
        setErrorValues(errorValues);
    };

    const handleChangeDate = (date) => {
        const modifiedDate = new Date(date);
        setArtistInfoValues((prevInfo) => {
            return {
                ...prevInfo,
                dob: modifiedDate,
            };
        });
    };

    const handleChangeSelect = (name, value) => {
        setArtistInfoValues((prevInfo) => {
            return {
                ...prevInfo,
                [name]: value,
            };
        });
    };

    const handleChangeValue = (event) => {
        const { name, value } = event.target;
        setArtistInfoValues((prevInfo) => {
            return {
                ...prevInfo,
                [name]: value,
            };
        });
        delete errorValues[name];
        setErrorValues(errorValues);
    };

    const handleRemoveImage = () => {
        setArtistInfoValues((prevInfo) => {
            return {
                ...prevInfo,
                profile: null,
            };
        });
    };

    const handleUpdateArtist = async (event) => {
        event.preventDefault();

        const checkGeneralInfo = checkFormValidate({
            profile: artistInfoValues["profile"],
            nameENG: artistInfoValues["nameENG"],
            nameMM: artistInfoValues["nameMM"],
            chosenType: artistInfoValues["chosenType"],
            bioENG: artistInfoValues["bioENG"],
            bioMM: artistInfoValues["bioMM"],
            dob: artistInfoValues["dob"],
            gender: artistInfoValues["gender"],
        });

        setErrorValues(checkGeneralInfo);

        const errorStateList = Object.keys(checkGeneralInfo);

        const { nameENG, nameMM } = artistInfoValues;

        if (
            errorStateList.length === 0 &&
            nameENG.length <= 100 &&
            nameMM.length <= 100
        ) {
            setArtistUpdateLoading(true);

            const fd = new FormData();
            
            fd.append("image", artistInfoValues.profile);
            
            const nameObject = {
                eng: artistInfoValues["nameENG"],
                mm: artistInfoValues["nameMM"]
            };
            
            const bioObject = {
                eng: artistInfoValues["bioENG"],
                mm: artistInfoValues["bioMM"]
            };
            
            fd.append("name", JSON.stringify(nameObject));
            fd.append("bio", JSON.stringify(bioObject));
            
            Object.keys(artistInfoValues).forEach((element) => {
                if (element !== "profile" && element !== "nameENG" && element !== "nameMM" && 
                    element !== "bioENG" && element !== "bioMM") {
                    element === "gender"
                        ? fd.append("gender", artistInfoValues["gender"] === "male" ? "male" : "female")
                        : element === "dob"
                        ? fd.append("dob", format(artistInfoValues["dob"], "yyyy-MM-dd"))
                        : fd.append(element, artistInfoValues[element]);
                }
            });

            const updateUrl = `${apiServer.artists.update}/${artist_Id}`;
            
            try {
                const response = await put(updateUrl, fd);
                const responseData = handleApiSuccess(response, setArtistUpdateLoading);
                
                navigate("/artists", {
                    replace: true,
                    state: { status: "success", message: responseData.message },
                });
            } catch (errors) {
                handleApiError(errors, setErrorUpdateArist, setArtistUpdateLoading);
            }
        }
    };

    if (getInfoError) return <EmptyPage text={getInfoError} />;

    if (getInfoLoading)
        return <SquareLoading text={"Retrieving Artist Information..."} />;

    if (artistUpdateLoading)
        return <PopupLoading text="Updating Artist Information..." />;

    return (
        <>
            <ArtistForm
                formTitle={"Update Artist"}
                onChangeValue={handleChangeValue}
                onUploadProfile={handleUploadImage}
                onRemoveProfile={handleRemoveImage}
                onChangeDate={handleChangeDate}
                onSelectValue={handleChangeSelect}
                infoValues={artistInfoValues}
                errorValues={errorValues}
                onCreate={handleUpdateArtist}
                onCreateCancel={handleCancelUpdate}
                formSubmitName={"Update"}
            />

            {errorUpdateArtist !== "" && (
                <AdaAlert type="error" message={errorUpdateArtist} />
            )}
        </>
    );
};

export default UpdateArtist;
